import { ShipTo } from 'src/types/orderHeaderDropdowns';
import { toString } from '@progress/kendo-intl';


export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export function FirstOrNull(obj: any) {
  if (obj === null || obj === undefined) return null;

  for (var i in obj) {
    if (obj.hasOwnProperty(i)) {
      return obj[i];
    }
  }
  return null;
}

export function RoundTo(n: any, digits: number): number {
  if (n === null) return n;
  if (n === undefined) return n;

  var negative = false;
  if (digits === undefined) {
    digits = 0;
  }
  if (n < 0) {
    negative = true;
    n = n * -1;
  }
  var multiplicator = Math.pow(10, digits);
  n = parseFloat((n * multiplicator).toFixed(11));
  n = (Math.round(n) / multiplicator).toFixed(digits);
  if (negative) {
    n = (n * -1).toFixed(digits);
  }
  return n * 1;
}

export function RoundUp(n: any, digits: number): number {
  if (n === null) return n;
  if (n === undefined) return n;

  var negative = false;
  if (digits === undefined) {
    digits = 0;
  }
  if (n < 0) {
    negative = true;
    n = n * -1;
  }
  var multiplicator = Math.pow(10, digits);
  n = parseFloat((n * multiplicator).toFixed(11));
  n = (Math.ceil(n) / multiplicator).toFixed(digits);
  if (negative) {
    n = (n * -1).toFixed(digits);
  }
  return n * 1;
}

//Round up to the nearest multiple
export function RoundUpToNextMultiple(num: number, multiple: number): number {
  if (num === 0) return 0;
  if (multiple === 0) return num;

  let remainder: number = num % multiple;

  if (remainder === 0) return num;

  let result: number = num + multiple - remainder;
  return result;
}

export function CalculateMargin(price: number, cost: number) {
  if (price == 0) return 0;
  return ((price - cost) / price) * 100;
}

const ROUTE_DAY_OF_WEEK_NAMES: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export function getDeliveryDaysOfWeekFromRoute(route: string): number[] {
  if (!route || route.length < 5) {
    return [];
  }

  const deliveryDays: number[] = [];
  const dod1 = parseInt(route.charAt(3), 10);
  const dod2 = parseInt(route.charAt(4), 10);

  if (!Number.isNaN(dod1) && dod1 > 0 && dod1 < 8) {
    deliveryDays.push(dod1);
  }
  if (!Number.isNaN(dod2) && dod2 > 0 && dod2 < 8) {
    deliveryDays.push(dod2);
  }

  return deliveryDays;
}

export function routeDeliveryDayToJsDay(routeDay: number): number {
  return routeDay === 7 ? 0 : routeDay;
}

export function getJsDaysOfWeekFromRoute(route: string): number[] {
  return getDeliveryDaysOfWeekFromRoute(route).map(routeDeliveryDayToJsDay);
}

export function GetDayOfWeekFromRoute(route: string): string {
  const deliveryDays = getDeliveryDaysOfWeekFromRoute(route);
  if (deliveryDays.length === 0) {
    return '';
  }

  return deliveryDays
    .map((day) => ROUTE_DAY_OF_WEEK_NAMES[day] ?? '')
    .filter(Boolean)
    .join(', ');
}

export function isDateOnRouteDeliveryDay(dateIso: string, route: string): boolean {
  const deliveryDateDow = GetDayOfWeekFromDate(dateIso);
  const routeDays = GetDayOfWeekFromRoute(route);
  if (!routeDays || !deliveryDateDow) {
    return true;
  }
  return routeDays.split(', ').includes(deliveryDateDow);
}

export function alignDateToRouteDeliveryDays(date: Date, route: string): Date {
  const jsDays = getJsDaysOfWeekFromRoute(route);
  if (jsDays.length === 0) {
    return date;
  }

  const result = new Date(date);
  while (!jsDays.includes(result.getDay())) {
    result.setDate(result.getDate() + 1);
  }
  return result;
}

export function GetDayOfWeekFromDate(date: string): string {
  if (!date) return '';
  let ds = date.toString();
  if (ds.indexOf('T') > 0) {
    ds = date.substring(0, ds.indexOf('T'));
  }
  let d = new Date(ds);
  d.setDate(d.getDate() + 1);

  let num = d.getDay();
  if (num == 0) return 'Sunday';
  if (num == 1) return 'Monday';
  if (num == 2) return 'Tuesday';
  if (num == 3) return 'Wednesday';
  if (num == 4) return 'Thursday';
  if (num == 5) return 'Friday';
  if (num == 6) return 'Saturday';
}

export function GetOrderTypeFullName(orderType: string): string {
  if (orderType === 'W') return 'Warehouse';
  if (orderType === 'D') return 'Direct';
  if (orderType === 'P') return 'Plant';
  return '';
}

export function formatPhoneNumber(phoneNumberString: string): string {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return null;
}

export const isNullOrEmpty = (value: string | null | undefined): boolean => {
  return value === null || value === undefined || value.trim() === '';
};

export function isEmptyObject(obj: any): boolean {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export function isBfgRouteCarrier(carrier: string | undefined): boolean {
  if (!carrier) {
    return false;
  }

  const normalizedCarrier = carrier.toLowerCase();
  return normalizedCarrier === 'bfg' || normalizedCarrier.startsWith('l');
}

export function getRouteDeliveryDayLabel(route: string | undefined, carrier: string | undefined): string {
  if (!isBfgRouteCarrier(carrier) || !route) {
    return '';
  }

  return GetDayOfWeekFromRoute(route);
}

export function getDefaultShipDateByRoute(options: {
  leadTimeDays?: number | null;
  alignToRoute?: boolean;
  route?: string;
}): Date {
  let defaultShipDate = new Date();
  defaultShipDate.setHours(0, 0, 0, 0);
  defaultShipDate.setDate(defaultShipDate.getDate() + 1);

  if (options.leadTimeDays && options.leadTimeDays > 0) {
    defaultShipDate.setDate(defaultShipDate.getDate() + options.leadTimeDays);
  }

  if (defaultShipDate.getDay() === 6) {
    defaultShipDate.setDate(defaultShipDate.getDate() + 2);
  }

  if (defaultShipDate.getDay() === 0) {
    defaultShipDate.setDate(defaultShipDate.getDate() + 1);
  }

  if (options.alignToRoute !== false && options.route) {
    defaultShipDate = alignDateToRouteDeliveryDays(defaultShipDate, options.route);
  }

  return defaultShipDate;
}

export function shouldApplyRouteShipDateRestrictions(
  warehouseCode: string | undefined,
  shipTo: Pick<ShipTo, 'warehouseCode'> | undefined
): boolean {
  if (!warehouseCode || !shipTo?.warehouseCode) return true;
  return warehouseCode.trim() === shipTo.warehouseCode.trim();
}

export function GetDeliveryMethodDay(shipTo: ShipTo): string {
  return getRouteDeliveryDayLabel(shipTo?.route, shipTo?.carrier);
}

export function formatNumber(value: number, format?: string): string {
  const formatMap = {
    '{0:c2}': 'c2',
    '{0:p0}': 'p0',
    '{0:p}': 'p',
  };
  
  const f = formatMap[format] || format;
  
  return toString(value, f);
}

export function formatDate(value: Date, format?: string): string {
  return toString(value, format);
}
